import type { OperationHistoryDto } from "jadro-pico/dtos/pico_com/OperationHistoryDto";
import type { PicoAppDto } from "jadro-pico/apps/PicoAppDto";
import { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import { PICO_APPS } from "jadro-pico/apps/picoApps";
import { HUB_OPERATIONS_PER_APP } from "jadro-pico/constants/operations";
import {
  historyRowToQueueItem,
  mergeRequestQueue,
  type RequestQueueItem,
} from "./requestQueueItems";
import type { PendingMutation } from "./pendingMutationsDb";

export interface HubSection {
  id: PicoAppEnum;
  name: string;
  items: RequestQueueItem[];
  /** Absent while the catalog is unreachable, which is what makes the section unopenable. */
  feUrl?: string;
  hostsQueue: boolean;
}

interface HubSectionsInput {
  app: PicoAppEnum;
  rows: OperationHistoryDto[];
  outbox: PendingMutation[];
  catalog: PicoAppDto[];
}

// A full window means the server sent everything it has for that app, so "missing from history" is
// trustworthy; a truncated one is not, and a failed entry would be labelled as never sent when it
// may well be stored further back. Those are left to the app's own page, which reads a hundred.
function outboxFor(appRows: OperationHistoryDto[], outbox: PendingMutation[]): PendingMutation[] {
  return appRows.length < HUB_OPERATIONS_PER_APP
    ? outbox
    : outbox.filter((entry) => entry.status !== "failed");
}

// Identity comes from the static app list, never from the fetched catalog: the outbox has to stay
// reachable when the server cannot be asked at all, and a row the server did return must not
// disappear just because a second request for the addresses failed.
export function buildHubSections({ app, rows, outbox, catalog }: HubSectionsInput): HubSection[] {
  // Own app first: it is the only one that can hold something not yet on the server.
  const ordered = [...PICO_APPS].sort((a, b) => Number(b.id === app) - Number(a.id === app));

  return (
    ordered
      .map((entry) => {
        const appRows = rows.filter((row) => row.app === entry.id);
        const items =
          entry.id === app
            ? mergeRequestQueue(outboxFor(appRows, outbox), appRows)
            : appRows.map(historyRowToQueueItem);
        return {
          id: entry.id,
          name: entry.name,
          items,
          feUrl: catalog.find((listed) => listed.id === entry.id)?.feUrl,
          hostsQueue: entry.hasRequestQueue,
        };
      })
      // An app that hosts a queue keeps its section even with nothing in it: the outbox is bound to
      // that app's own origin, so this is the only way to reach an unsent save the server never saw.
      .filter((section) => section.hostsQueue || section.items.length > 0)
  );
}
